// ============================================================
// HOOKED — HookedWorld3D.js        HOOKED'S OWN POCKET
//
// caP'n, 2026-09-25: "once you enter the Hooked building, it looks
// you're facing the hooked building again, it looks like you're in the
// exact same spot, a pier... but once you look around you realize the
// pier is entirely gone, only water, and islands(eventually) … I still
// want that quick sand bar though so when you look back the pier is
// connected to the old sand bar (… everything etc will be blender only)"
//
// Two Blender glbs, nothing hand-built here:
//   · shop_hooked.glb   THE LODGE (+ its fishing pier, davits, skiff) —
//                        the SAME file the Pier stands in its court;
//                        build_shop.py writes it here on every build
//   · hooked_world.glb  EVERYTHING AROUND IT — the lodge's own patch of
//                        the Pier (the Pier's own rail/post/pile parts),
//                        the boardwalk, the gangway, THE SAND BAR
//                        (builders/hooked_world_blender)
// Both are placed in the LODGE's frame, so they always line up. Both
// carry their own walk-maps in their extras (ie_shop, ie_world) — the
// screen walks exactly what is drawn. src/pierLink.json holds static
// copies (tools/syncPier.mjs) so you can walk from the first frame.
//
// LAWS IN FORCE (Hooked's): every canvas texture cached · colorSpace
// sRGB on every canvas · ZERO new lights (the glow is emissive).
// ============================================================

import { Component, Suspense, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame, useLoader } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import LINK from "./pierLink.json";

export const SEA_Y = -3.2;            // the Pier's sea level (pierDeck SEA_Y): the planks are at 0
const url = (f) => `${process.env.PUBLIC_URL || ""}/models/${f}`;
const ROT = { S: 0, N: Math.PI, E: Math.PI / 2, W: -Math.PI / 2 };


// ── a front's frame: origin = the middle of its frontage ON the edge line ──
export function frameOf(f) {
  const ns = f.face === "S" || f.face === "N";
  const mid = (f.a0 + f.a1) / 2;
  return { x: ns ? mid : f.edge, z: ns ? f.edge : mid, rot: ROT[f.face] ?? 0, w: f.a1 - f.a0, face: f.face };
}
export function localToWorld(fr, lx, lz) {
  const c = Math.cos(fr.rot), s = Math.sin(fr.rot);
  return [fr.x + lx * c + lz * s, fr.z - lx * s + lz * c];
}
// a shop-local rect [x0, x1, z0, z1] → a world AABB (every face is a multiple of 90°)
export function rectToWorld(fr, r) {
  const a = localToWorld(fr, r[0], r[2]), b = localToWorld(fr, r[1], r[3]);
  return [Math.min(a[0], b[0]), Math.max(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[1], b[1])];
}
export const inRect = (r, x, z, m = 0) => x >= r[0] - m && x <= r[1] + m && z >= r[2] - m && z <= r[3] + m;

// the lodge's frontage on the Pier (where it stands in its court), and both walk-maps —
// ⚠ guarded: a missing or stale pierLink.json must not crash the import
export const HOOKED = LINK.front || { id: "hooked", face: "S", edge: 4.5, a0: 49.3, a1: 53.7 };
export const HOOKED_FRAME = frameOf(HOOKED);
export const LODGE_META = LINK.hooked || null;
export const WORLD_META = LINK.world || null;

// ── cached canvas textures ──
const TEX = new Map();
function canvasTex(key, w, h, draw) {
  if (TEX.has(key)) return TEX.get(key);
  const c = document.createElement("canvas"); c.width = w; c.height = h;
  draw(c.getContext("2d"), w, h);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.needsUpdate = true;
  TEX.set(key, t); return t;
}
const grainTex = () => {
  const t = canvasTex("pier-grain", 256, 256, (g, w, h) => {
    g.fillStyle = "#c9a782"; g.fillRect(0, 0, w, h);          // warm wood: Hooked's moon is cooler than the Pier's lights
    let s = 7; const R = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
    for (let i = 0; i < 90; i++) {
      g.strokeStyle = `rgba(90,62,36,${0.05 + R() * 0.12})`; g.lineWidth = 1 + R() * 2.5;
      const y = R() * h; g.beginPath(); g.moveTo(0, y);
      for (let x = 0; x <= w; x += 16) g.lineTo(x, y + Math.sin(x * 0.03 + i) * 2.5);
      g.stroke();
    }
  });
  t.wrapS = t.wrapT = THREE.RepeatWrapping; return t;
};
const dotTex = () => canvasTex("pier-dot", 64, 64, (g, w) => {
  const grd = g.createRadialGradient(w / 2, w / 2, 0, w / 2, w / 2, w / 2);
  grd.addColorStop(0, "rgba(255,255,255,1)"); grd.addColorStop(0.3, "rgba(255,255,255,0.75)"); grd.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grd; g.fillRect(0, 0, w, w);
});
// the lodge's open door: warm lamplight from inside, dark at the edges, a figure-less glow
const doorTex = () => canvasTex("lodge-door", 128, 256, (g, w, h) => {
  g.fillStyle = "#140b06"; g.fillRect(0, 0, w, h);
  const grd = g.createRadialGradient(w / 2, h * 0.62, 4, w / 2, h * 0.62, h * 0.62);
  grd.addColorStop(0, "rgba(255,196,120,0.95)"); grd.addColorStop(0.45, "rgba(214,128,58,0.55)"); grd.addColorStop(1, "rgba(20,11,6,0)");
  g.fillStyle = grd; g.fillRect(0, 0, w, h);
  g.fillStyle = "rgba(255,210,150,0.25)"; g.fillRect(0, h * 0.9, w, h * 0.1);      // light on the threshold
});

// the Pier's own palette for its boardwalk (pierShell3D LOOK), so the two games match
const LOOK = {
  pier_plank: { color: "#e2d2b8", roughness: 0.88 },
  pier_slab: { color: "#3c2c1e", roughness: 1 },
  pier_fascia: { color: "#6e5438", roughness: 1 },
  pier_brace: { color: "#4a3a2a", roughness: 1 },
  pier_pile: { color: "#54402c", roughness: 1 },
  pier_fence: { color: "#8a6c4c", roughness: 0.95, emissive: "#2a1c10", emissiveIntensity: 0.3 },
  pier_rail: { color: "#a8865e", roughness: 0.85, emissive: "#4a3420", emissiveIntensity: 0.35 },
  pier_post: { color: "#96744e", roughness: 0.85, emissive: "#4a3420", emissiveIntensity: 0.3 },
  pier_iron: { color: "#3a3430", roughness: 0.45, metalness: 0.55, noMap: true },
};

class Boundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn(`[hooked] ${this.props.what} did not load`, e); }
  render() { return this.state.bad ? (this.props.fallback || null) : this.props.children; }
}

// ════════════════════════════════════════════════════════════
// THE LODGE — shop_hooked.glb, SOLID (you are in Hooked). It hands
// back its walk-map (ie_shop: walk, solid, board, boat, doorstep,
// fish) and the SKIFF (one object: the boat you actually sail).
// `inBoat` hides the slung skiff while you're out in it.
// ════════════════════════════════════════════════════════════
function LodgeModel({ inBoat, onReady, posRef }) {
  const gltf = useLoader(GLTFLoader, url("shop_hooked.glb"), (l) => l.setMeshoptDecoder(MeshoptDecoder));
  const built = useMemo(() => {
    const root = gltf.scene.clone(true);
    let meta = null; const slung = []; const slings = []; const mats = new Set();
    root.traverse((o) => {
      if (!meta && o.userData && o.userData.ie_shop) { try { meta = JSON.parse(o.userData.ie_shop); } catch (e) { meta = null; } }
      // ⚠ three's loader SANITISES node names: Blender's "hooked_boat.000" arrives as
      // "hooked_boat000" (the dot is stripped), so match both spellings
      if (/^hooked_(boat|glow_boatlamp)\.?\d+$/.test(o.name)) slung.push(o);
      if (o.name === "hooked_sling") slings.push(o);          // the davits' slings: Wharf3D draws live ones while you're out
      if (!o.isMesh) return;
      const glow = /(?:^|_)glow_/.test(o.name) || /(?:^|_)glow_/.test((o.parent && o.parent.name) || "");
      const list = Array.isArray(o.material) ? o.material : [o.material];
      const next = list.map((mm) => { const c = mm.clone(); if (glow) { c.toneMapped = false; if (c.emissive) c.emissive.copy(c.color); } mats.add(c); return c; });
      o.material = next.length === 1 ? next[0] : next;
      o.castShadow = !glow; o.receiveShadow = true;
    });
    // the sailing skiff: a clone of the slung one, brought to its own origin
    // (the boat and its lamp are sibling copies at the same spot, so zeroing both keeps
    // their layout; a node already inside another slung node is carried by it, not re-cloned)
    const own = slung.filter((o) => { let p = o.parent; while (p) { if (slung.includes(p)) return false; p = p.parent; } return true; });
    const hull = new THREE.Group();
    own.forEach((o) => { const c = o.clone(true); c.position.set(0, 0, 0); c.rotation.set(0, 0, 0); hull.add(c); });
    // ⚠ the skiff's bow is −z in its glb (Boat3D's convention, which sails STERN-first:
    // movement is along +z). Turned once here, so the bow and its lantern lead.
    hull.rotation.y = Math.PI;
    // the sling points on the hull (boat-local, from the lodge's own meta): the live ropes tie on here
    const holds = ((meta && meta.sling && meta.sling.hold) || []).map((h) => { const a = new THREE.Object3D(); a.position.set(h[0], h[1], h[2]); hull.add(a); return a; });
    const skiff = own.length ? new THREE.Group().add(hull) : null;
    if (skiff) skiff.userData.holds = holds;
    return { root, meta, slung, slings, skiff, mats: [...mats] };
  }, [gltf]);
  useEffect(() => { built.slung.forEach((o) => { o.visible = !inBoat; }); built.slings.forEach((o) => { o.visible = !inBoat; }); }, [built, inBoat]);
  useEffect(() => { onReady && onReady(built.meta, built.skiff); }, [built, onReady]);
  useEffect(() => () => built.mats.forEach((m) => m.dispose()), [built]);
  const fr = HOOKED_FRAME;
  const door = (built.meta && built.meta.door) || [0, 0.14, 1.05, 1.72];

  // ⭐ THE FADER (the Pier's rule: "buildings between the lens and the player fade").
  // Hooked's camera trails you, so facing the sand bar from the court put the lens
  // INSIDE the lodge's roof (visual review, 2026-09-25). When the lodge's box holds
  // the camera or stands between it and you, the lodge fades to a clean cutaway,
  // and comes back when you're clear. One box, one ray, no allocation per frame.
  const shell = useRef(); const k = useRef(1);
  const fx = useMemo(() => {
    const m = (built.meta && built.meta.mass) || [-2.2, 0, -3.35, 2.2, 4.45, -0.02];
    const box = new THREE.Box3(new THREE.Vector3(fr.x + m[0] - 0.4, -0.5, fr.z + m[2] - 0.4),
                               new THREE.Vector3(fr.x + m[3] + 0.4, m[4] + 1.6, fr.z + 1.25));   // + the bass on the ridge, + the portico
    return { box, ray: new THREE.Ray(), to: new THREE.Vector3(), hit: new THREE.Vector3(), base: built.mats.map((mm) => mm.opacity) };
  }, [built, fr.x, fr.z]);
  useFrame(({ camera }, dt) => {
    const p = posRef && posRef.current; if (!p || !shell.current) return;
    const { box, ray, to, hit } = fx;
    to.set(p.x, p.y + 1.2, p.z);
    let blocks = box.containsPoint(camera.position);
    if (!blocks && !box.containsPoint(to)) {
      ray.origin.copy(camera.position); ray.direction.copy(to).sub(camera.position);
      const d = ray.direction.length(); ray.direction.multiplyScalar(1 / (d || 1));
      blocks = !!ray.intersectBox(box, hit) && hit.distanceTo(camera.position) < d;
    }
    const want = blocks ? 0 : 1;
    if (Math.abs(want - k.current) < 0.001 && (k.current === 0 || k.current === 1)) return;
    k.current += (want - k.current) * Math.min(1, dt * 7);
    if (Math.abs(want - k.current) < 0.02) k.current = want;
    const faded = k.current < 1;
    shell.current.visible = k.current > 0.03;              // fully faded: a clean cutaway
    built.mats.forEach((mm, i) => {
      if (mm.transparent !== faded && !(fx.base[i] < 1)) { mm.transparent = faded; mm.needsUpdate = true; }
      mm.opacity = fx.base[i] * k.current; mm.depthWrite = !faded;
    });
  });
  return (
    <group ref={shell} position={[fr.x, 0, fr.z]} rotation={[0, fr.rot, 0]}>
      <primitive object={built.root} />
      {/* in Hooked the door is Wylly's: warm light spilling out, not a rift */}
      <mesh position={[door[0], door[1] + door[3] / 2, 0.03]}>
        <planeGeometry args={[door[2], door[3]]} />
        <meshBasicMaterial map={doorTex()} transparent opacity={0.9} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh position={[door[0], 0.02, 0.95]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[2.4, 1.8]} />
        <meshBasicMaterial map={dotTex()} color="#ffb866" transparent opacity={0.45} depthWrite={false}
          blending={THREE.AdditiveBlending} toneMapped={false} />
      </mesh>
    </group>
  );
}


// ════════════════════════════════════════════════════════════
// THE WORLD — hooked_world.glb: the court, the boardwalk, the
// gangway, the sand bar. The Pier's own parts are repainted with
// the Pier's LOOK by material NAME (the two games match); the sand
// bar's own materials come as Blender made them. glow_* is lit.
// ════════════════════════════════════════════════════════════
function WorldModel({ onReady }) {
  const gltf = useLoader(GLTFLoader, url("hooked_world.glb"), (l) => l.setMeshoptDecoder(MeshoptDecoder));
  const built = useMemo(() => {
    const root = gltf.scene.clone(true);
    const grain = grainTex(); const made = {}; const own = new Set();
    let meta = null;
    root.traverse((o) => { if (!meta && o.userData && o.userData.ie_world) { try { meta = JSON.parse(o.userData.ie_world); } catch (e) { meta = null; } } });
    const pierMat = (name, vc) => {
      const key = name + (vc ? "|vc" : "");
      if (made[key]) return made[key];
      const { noMap, ...look } = LOOK[name];
      made[key] = new THREE.MeshStandardMaterial({ ...look, map: noMap ? null : grain, vertexColors: vc, metalness: look.metalness || 0 });
      return made[key];
    };
    root.traverse((o) => {
      if (!o.isMesh) return;
      const glow = /(?:^|_)glow_/.test(o.name) || /(?:^|_)glow_/.test((o.parent && o.parent.name) || "");
      const list = Array.isArray(o.material) ? o.material : [o.material];
      const next = list.map((mm) => {
        const name = ((mm && mm.name) || "").replace(/\.\d+$/, "");
        if (LOOK[name]) return pierMat(name, !!o.geometry.attributes.color);
        const c = mm.clone(); if (glow) { c.toneMapped = false; if (c.emissive) c.emissive.copy(c.color); } own.add(c); return c;
      });
      o.material = next.length === 1 ? next[0] : next;
      o.frustumCulled = !o.isInstancedMesh;          // an instanced mesh's bounds are one copy's — never cull it
      o.castShadow = !glow && !o.isInstancedMesh; o.receiveShadow = true;
    });
    return { root, meta, mats: [...Object.values(made), ...own] };
  }, [gltf]);
  useEffect(() => { onReady && onReady(built.meta); }, [built, onReady]);
  useEffect(() => () => built.mats.forEach((m) => m.dispose()), [built]);
  const fr = HOOKED_FRAME;
  return <group position={[fr.x, 0, fr.z]} rotation={[0, fr.rot, 0]}><primitive object={built.root} /></group>;
}

// ════════════════════════════════════════════════════════════
// THE ONE EXPORT the screen mounts
// ════════════════════════════════════════════════════════════
export function HookedWorld3D({ inBoat, onLodge, onWorld, posRef }) {
  return (
    <group>
      <Boundary what="hooked_world.glb"><Suspense fallback={null}><WorldModel onReady={onWorld} /></Suspense></Boundary>
      <Boundary what="shop_hooked.glb"><Suspense fallback={null}><LodgeModel inBoat={inBoat} onReady={onLodge} posRef={posRef} /></Suspense></Boundary>
    </group>
  );
}
